import { useSearchParams } from "react-router-dom";
import { trpc } from "../lib/trpc";
import { PAYMENT_METHOD_LABEL } from "../components/purchasing/PaymentTermsFields";

const CUR_SYM: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
function money(n: number | null, cur: string): string {
  if (n == null) return "—";
  return `${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n)} ${CUR_SYM[cur] ?? cur}`;
}
function fmtDate(d: Date | string | null): string {
  return d ? new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d)) : "—";
}
const STATUS_LABEL: Record<string, string> = {
  draft: "Taslak", confirmed: "Onaylı", partially_received: "Kısmi Teslim", received: "Teslim Alındı", cancelled: "İptal",
};

/** Kurumsal Satın Alma Sipariş Formu (A4) — siparişi veren firma + tedarikçi + kalemler + ödeme koşulları + imza/kaşe. */
export function PurchaseOrderFormPrintPage() {
  const [params] = useSearchParams();
  const id = params.get("id") ?? "";
  const q = trpc.purchasing.get.useQuery({ id }, { enabled: !!id });
  const profQ = trpc.settings.companyProfile.get.useQuery(undefined, { staleTime: 5 * 60_000 });
  const p = q.data;
  const supId = p?.supplierId ?? "";
  const supQ = trpc.supplier.get.useQuery({ id: supId }, { enabled: !!supId });
  const prof = profQ.data;
  const sup = supQ.data;
  const cur = p?.currency ?? "TRY";

  const payLine = p?.paymentMethod
    ? [
        PAYMENT_METHOD_LABEL[p.paymentMethod] ?? p.paymentMethod,
        p.paymentMethod === "term" && p.termDays != null ? `${p.termDays} gün vade` : null,
        p.paymentMethod === "credit_card" && p.installmentCount ? `${p.installmentCount} taksit` : null,
        p.paymentMethod === "credit_card" && p.banks.length ? p.banks.map((b) => b.name).join(", ") : null,
      ].filter(Boolean).join(" · ")
    : "Belirtilmedi";

  return (
    <div className="pof-root">
      <style>{`
        .pof-root { --ink:#14253E; --line:#c9d2df; --muted:#5b6472; --soft:#f4f6fa; background:#fff; color:#1a2230; }
        .pof-page { max-width: 820px; margin: 0 auto; padding: 22px 26px 40px; font-family: 'Montserrat', system-ui, sans-serif; font-size: 12px; }
        .pof-num { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        .pof-head { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; border-bottom: 3px solid var(--ink); padding-bottom: 12px; margin-bottom: 0; }
        .pof-brand { display:flex; align-items:center; gap:12px; }
        .pof-logo { max-height: 56px; max-width: 150px; object-fit: contain; }
        .pof-brand .n { font-size: 16px; font-weight: 800; color: var(--ink); }
        .pof-brand .a { font-size: 10.5px; color: var(--muted); margin-top:2px; max-width: 320px; line-height:1.35; }
        .pof-doc { text-align:right; }
        .pof-doc .t { font-size: 17px; font-weight: 800; color: var(--ink); letter-spacing:.02em; }
        .pof-doc .row { font-size: 11px; color: var(--muted); margin-top: 3px; }
        .pof-doc .row b { color: var(--ink); }
        .pof-status { display:inline-block; font-size:10px; font-weight:700; padding:2px 8px; border-radius:4px; background:var(--soft); color:var(--ink); border:1px solid var(--line); }
        .pof-parties { display:grid; grid-template-columns: 1fr 1fr; gap: 0; border:1px solid var(--line); border-top:none; }
        .pof-party { padding: 10px 14px; }
        .pof-party + .pof-party { border-left:1px solid var(--line); }
        .pof-party .lbl { font-size: 9px; font-weight:800; letter-spacing:.06em; text-transform:uppercase; color: var(--muted); margin-bottom: 4px; }
        .pof-party .nm { font-size: 13px; font-weight:700; color: var(--ink); }
        .pof-party .ln { font-size: 11px; color:#37414f; margin-top:2px; line-height:1.4; }
        .pof-meta { display:grid; grid-template-columns: repeat(4, 1fr); border:1px solid var(--line); border-top:none; }
        .pof-meta .c { padding: 7px 12px; }
        .pof-meta .c + .c { border-left:1px solid var(--line); }
        .pof-meta .k { font-size: 8.5px; font-weight:700; letter-spacing:.05em; text-transform:uppercase; color: var(--muted); }
        .pof-meta .v { font-size: 11.5px; font-weight:600; color: var(--ink); margin-top:1px; }
        .pof-sec { font-size: 11px; font-weight:800; letter-spacing:.04em; text-transform:uppercase; color: var(--ink); margin: 16px 0 6px; }
        table.pof-t { width:100%; border-collapse: collapse; font-size: 11px; }
        table.pof-t th { text-align:left; font-size: 9px; font-weight:800; letter-spacing:.04em; text-transform:uppercase; color:#fff; background: var(--ink); padding:6px 8px; }
        table.pof-t td { padding:6px 8px; border-bottom:1px solid #e7ebf1; }
        table.pof-t td.n, table.pof-t th.n { text-align:right; font-family:'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        table.pof-t tbody tr:nth-child(even) td { background:#fafbfd; }
        table.pof-t tfoot td { font-weight:800; border-top:2px solid var(--ink); border-bottom:none; color: var(--ink); font-size:12.5px; }
        .pof-code { font-family:'JetBrains Mono', monospace; font-size: 9px; color: var(--muted); }
        .pof-pay { border:1px solid var(--line); border-radius:6px; padding:9px 12px; font-size:11.5px; }
        .pof-pay b { color: var(--ink); }
        .pof-note { font-size:11px; color:#37414f; margin-top:6px; }
        .pof-sign { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-top: 40px; }
        .pof-sign .box { border-top:1px solid var(--ink); padding-top:6px; font-size:10.5px; color: var(--muted); text-align:center; }
        .pof-foot { margin-top: 22px; padding-top:8px; border-top:1px solid var(--line); font-size:9.5px; color:var(--muted); text-align:center; }
        .pof-toolbar { position: sticky; top: 0; z-index: 5; background:#fff; display:flex; gap:12px; align-items:center; justify-content:flex-end; padding: 10px 26px; border-bottom:1px solid var(--line); }
        .pof-btn { border:1px solid var(--ink); background: var(--ink); color:#fff; font-weight:600; font-size:13px; padding:7px 16px; border-radius:6px; cursor:pointer; }
        .pof-btn.sec { background:#fff; color: var(--ink); }
        .pof-btn:disabled { opacity:.5; cursor:default; }
        @media print {
          .pof-toolbar { display:none !important; }
          .pof-page { max-width:none; padding:0; }
          .pof-sign { margin-top: 48px; }
          .pof-sec, .pof-head { break-after: avoid; }
          tr { break-inside: avoid; }
        }
        @page { size: A4 portrait; margin: 12mm; }
      `}</style>

      <div className="pof-toolbar">
        <button type="button" className="pof-btn sec" onClick={() => window.close()}>Kapat</button>
        <button type="button" className="pof-btn" onClick={() => window.print()} disabled={!p}>Yazdır</button>
      </div>

      <div className="pof-page">
        {!id ? (
          <div style={{ color: "#b91c1c" }}>Geçersiz bağlantı — sipariş kimliği yok.</div>
        ) : q.isLoading ? (
          <div style={{ color: "var(--muted)" }}>Sipariş formu hazırlanıyor…</div>
        ) : q.isError ? (
          <div style={{ color: "#b91c1c" }}>{q.error?.message}</div>
        ) : p ? (
          <>
            {/* Başlık: siparişi veren firma + belge */}
            <div className="pof-head">
              <div className="pof-brand">
                {prof?.logoUrl ? <img className="pof-logo" src={prof.logoUrl} alt={prof.name ?? "Logo"} /> : null}
                <div>
                  <div className="n">{prof?.name || "STEPERP"}</div>
                  {prof?.address ? <div className="a">{prof.address}</div> : null}
                </div>
              </div>
              <div className="pof-doc">
                <div className="t">SATIN ALMA SİPARİŞİ</div>
                <div className="row">No: <b className="pof-num">{p.code}</b></div>
                <div className="row">Tarih: <b className="pof-num">{fmtDate(p.orderDate)}</b></div>
                <div className="row" style={{ marginTop: 5 }}><span className="pof-status">{STATUS_LABEL[p.status] ?? p.status}</span></div>
              </div>
            </div>

            {/* Taraflar: siparişi veren / tedarikçi */}
            <div className="pof-parties">
              <div className="pof-party">
                <div className="lbl">Siparişi Veren</div>
                <div className="nm">{prof?.name || "STEPERP"}</div>
                {prof?.address ? <div className="ln">{prof.address}</div> : null}
                {prof?.taxOffice || prof?.taxNumber ? <div className="ln">Vergi D./No: {[prof?.taxOffice, prof?.taxNumber].filter(Boolean).join(" · ")}</div> : null}
                {prof?.phone ? <div className="ln">Tel: {prof.phone}</div> : null}
                {prof?.email ? <div className="ln">E-posta: {prof.email}</div> : null}
              </div>
              <div className="pof-party">
                <div className="lbl">Tedarikçi (Siparişi Alan)</div>
                <div className="nm">{p.supplierName ?? "— atanmadı —"}{p.supplierCode ? <span className="pof-code"> · {p.supplierCode}</span> : null}</div>
                {sup?.taxNumber ? <div className="ln">Vergi No: {sup.taxNumber}</div> : null}
                {sup?.address || sup?.city ? <div className="ln">{[sup?.address, sup?.city, sup?.country].filter(Boolean).join(", ")}</div> : null}
                {sup?.phone ? <div className="ln">Tel: {sup.phone}</div> : null}
                {sup?.email ? <div className="ln">E-posta: {sup.email}</div> : null}
              </div>
            </div>

            {/* Meta */}
            <div className="pof-meta">
              <div className="c"><div className="k">Talep Eden</div><div className="v">{p.requestedBy ?? "—"}</div></div>
              <div className="c"><div className="k">Onaylayan</div><div className="v">{p.approvedBy ?? "—"}{p.approvedAt ? ` · ${fmtDate(p.approvedAt)}` : ""}</div></div>
              <div className="c"><div className="k">Sipariş Tarihi</div><div className="v pof-num">{fmtDate(p.orderDate)}</div></div>
              <div className="c"><div className="k">Beklenen Teslim</div><div className="v pof-num">{fmtDate(p.expectedDate)}</div></div>
            </div>

            {/* Kalemler */}
            <div className="pof-sec">Sipariş Kalemleri</div>
            <table className="pof-t">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Malzeme</th>
                  <th className="n">Miktar</th>
                  <th>Birim</th>
                  <th className="n">Birim Fiyat</th>
                  <th className="n">Tutar</th>
                </tr>
              </thead>
              <tbody>
                {p.lines.map((l, i) => (
                  <tr key={l.id}>
                    <td className="n">{i + 1}</td>
                    <td><span className="pof-code">{l.itemCode}</span> {l.itemName}</td>
                    <td className="n">{new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(l.qty)}</td>
                    <td>{l.uom}</td>
                    <td className="n">{money(l.unitPrice, cur)}</td>
                    <td className="n">{money(l.lineTotal, cur)}</td>
                  </tr>
                ))}
              </tbody>
              {p.grandTotal != null ? (
                <tfoot><tr><td colSpan={5} style={{ textAlign: "right" }}>GENEL TOPLAM</td><td className="n">{money(p.grandTotal, cur)}</td></tr></tfoot>
              ) : null}
            </table>

            {/* Ödeme koşulları */}
            <div className="pof-sec">Ödeme Koşulları</div>
            <div className="pof-pay">
              <b>Ödeme:</b> {payLine} &nbsp;·&nbsp; <b>Para Birimi:</b> {cur}
              {p.paymentNotes ? <div className="pof-note">Not: {p.paymentNotes}</div> : null}
            </div>
            {p.notes ? <div className="pof-note">Sipariş Notu: {p.notes}</div> : null}

            {/* İmza / kaşe */}
            <div className="pof-sign">
              <div className="box">Siparişi Veren<br />(Kaşe / İmza)</div>
              <div className="box">Tedarikçi Onayı<br />(Kaşe / İmza)</div>
            </div>

            <div className="pof-foot">Bu belge {prof?.name || "STEPERP"} tarafından düzenlenmiştir · {p.code} · {fmtDate(p.orderDate)}</div>
          </>
        ) : null}
      </div>
    </div>
  );
}
